// Direct request/response helpers for the Twilio Agent admin settings modal
// (super-user only; the goserver enforces the SUPER_USER check on every
// mutation). These hit the /v2/twilio_call/admin/* endpoints and unwrap the
// standard house envelope (r.data.data), mirroring how support_inbox's
// api_helpers.js handles its own admin calls. Every call rejects with the axios
// error so callers can surface the server's `reason` via apiErrorReason.

import {
  get_api as getApi,
  post_api as postApi,
  put_api as putApi,
  delete_api as deleteApi
} from '../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../utils/urls';

// Mirror the getUrl helper in index.jsx: prefer the dedicated twilio_call base
// URL when configured, else fall back to the environment-aware CMS API URL
// ('/v2/...' auto-routes to the Go server).
const getUrl = (endpoint) => {
  if (import.meta.env.VITE_TWILIO_CALL_BASE_URL) {
    return `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`;
  }
  return getApiUrl(endpoint);
};

const BASE = '/v2/twilio_call/admin';

// Pull the human-readable failure reason out of an axios error (house envelope
// {"status":"failure","reason":...}), falling back to a caller-supplied default.
export const apiErrorReason = (error, fallback) => {
  const data = error && error.response && error.response.data;
  return (data && (data.reason || data.message)) || fallback;
};

// GET /teams → [{ uuid, name, members: [{ cms_user_uuid, name }] }].
export const fetchTeams = () =>
  getApi(getUrl(`${BASE}/teams`), true).then(
    (r) => (r.data && r.data.data && r.data.data.teams) || []
  );

// POST /teams { name } → created team. SUPER_USER only.
export const createTeam = (name) =>
  postApi(getUrl(`${BASE}/teams`), { name }, true).then((r) => (r.data && r.data.data) || {});

// PUT /teams/:uuid { name } — rename.
export const renameTeam = (uuid, name) =>
  putApi(getUrl(`${BASE}/teams/${uuid}`), { name }, true).then(
    (r) => (r.data && r.data.data) || {}
  );

// DELETE /teams/:uuid.
export const deleteTeam = (uuid) => deleteApi(getUrl(`${BASE}/teams/${uuid}`), true);

// POST /teams/:uuid/members { cms_user_uuid } — add a member.
export const addTeamMember = (teamUuid, cmsUserUuid) =>
  postApi(getUrl(`${BASE}/teams/${teamUuid}/members`), { cms_user_uuid: cmsUserUuid }, true);

// DELETE /teams/:uuid/members/:cms_user_uuid — remove a member.
export const removeTeamMember = (teamUuid, cmsUserUuid) =>
  deleteApi(getUrl(`${BASE}/teams/${teamUuid}/members/${cmsUserUuid}`), true);

// GET /agents → [{ uuid, name, email }]. CMS users eligible for team membership.
export const fetchAgents = () =>
  getApi(getUrl(`${BASE}/agents`), true).then(
    (r) => (r.data && r.data.data && r.data.data.agents) || []
  );

// GET /phone_numbers → ["+13203029642", ...]. The env-appropriate Twilio caller
// numbers, flat and deduped, that a number can be routed from. SUPER_USER only.
export const fetchPhoneNumbers = () =>
  getApi(getUrl(`${BASE}/phone_numbers`), true).then(
    (r) => (r.data && r.data.data && r.data.data.phone_numbers) || []
  );

// GET /caller_types → [{ value, label }]. The caller categories a rule can match
// (e.g. ANY, TEACHER, TRIAL_STUDENT, MEMBER_STUDENT, NOMNOT_STUDENT). The label is
// server-supplied and shown verbatim in the UI.
export const fetchCallerTypes = () =>
  getApi(getUrl(`${BASE}/caller_types`), true).then(
    (r) => (r.data && r.data.data && r.data.data.caller_types) || []
  );

// GET /routing → [{ phone_number, rules: [{ caller_type, teams: [{ id, team_uuid,
// name, position }] }] }]. Rules are pre-ordered (ANY first) and each rule's teams
// are pre-ordered by position.
export const fetchRouting = () =>
  getApi(getUrl(`${BASE}/routing`), true).then(
    (r) => (r.data && r.data.data && r.data.data.routing) || []
  );

// POST /routing { phone_number, team_uuid, caller_type } — append a team to the ring
// order for a (number, caller_type) rule. caller_type is one of the enum values.
export const addRouting = (phoneNumber, teamUuid, callerType) =>
  postApi(
    getUrl(`${BASE}/routing`),
    { phone_number: phoneNumber, team_uuid: teamUuid, caller_type: callerType },
    true
  ).then((r) => (r.data && r.data.data) || {});

// DELETE /routing/:id — remove a single routing row.
export const deleteRouting = (id) => deleteApi(getUrl(`${BASE}/routing/${id}`), true);

// PUT /routing/reorder { phone_number, caller_type, ids:[...] } — full ordered id list
// for that (number, caller_type) rule.
export const reorderRouting = (phoneNumber, callerType, ids) =>
  putApi(
    getUrl(`${BASE}/routing/reorder`),
    { phone_number: phoneNumber, caller_type: callerType, ids },
    true
  );
